import React, { useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, ArrowRight, Sparkles, Shield, FileText, MessageSquare, ListChecks, KeyRound } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

export default function BlueprintSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tier = params.get('tier') || 'self_serve';
  const paymentEmail = decodeURIComponent(params.get('email') || '');

  // iter 186.b - The 1.5s auto-redirect (added in iter 145 Section 5) is
  // gone. It shipped to production expecting BlueprintIntake to recognize
  // the `?paid=1` query param and skip the pricing step, but that consumer
  // was never wired (iter 157's `isPaidUser` keyed only on `authedUser.role`,
  // which is null until the welcome-email password-set loop completes).
  // Result: a first-time paying customer was auto-shoved into an anonymous
  // intake that could double-charge them at Step 2. The intake now handles
  // `?paid=1` correctly, and the manual "Start Your Blueprint" button below
  // remains the intentional forward step. See PRD iter 186.b for the full
  // trace.

  // Tier display names - iter 186 refresh: current tiers first,
  // legacy keys retained as (legacy) so old success-page bookmarks
  // still render sensibly.
  const tierNames = {
    blueprint_only: 'Blueprint Only',
    essentials: 'Essentials',
    guided: 'Guided',
    self_serve: 'Digital Blueprint (legacy)',
    kickstart: 'Relocation Kickstart (legacy)',
    relocation_kickstart: 'Relocation Kickstart (legacy)',
    full_coordination: 'Full Coordination (legacy)',
    concierge: 'Full Coordination (legacy)',
  };
  const tierName = tierNames[tier] || 'Blueprint';

  // iter 186.c - Fallback "Set your password" deeplink. Mirrors the
  // create_account_url used in the welcome_portal_access email
  // (backend/utils/email_templates.py:138) so a buyer who closes the
  // tab or misses the email can still self-serve portal access from
  // this page. Encoding: %40 for '@' and %2B for '+' to match the
  // email link exactly.
  const emailEncodedForPortal = (paymentEmail || '')
    .replace('@', '%40')
    .replace('+', '%2B');
  const createAccountUrl = paymentEmail
    ? `/create-account?email=${emailEncodedForPortal}&tier=${tier}`
    : `/create-account?tier=${tier}`;

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <SEO 
        title="Payment Successful - Your Blueprint is on the Way" 
        description="Your Mexico relocation blueprint is on its way to your inbox now." 
        path="/relocation/blueprint/success" 
      />
      <Header currentPage="relocation" />

      <section className="pt-[140px] sm:pt-[180px] pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          
          {/* Success Header - Clean, No Animation */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#2CA8A0]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#2CA8A0]" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#0A1F2E] mb-3">
              Payment Successful!
            </h1>
            <p className="text-[#2CA8A0] font-medium text-sm sm:text-base">
              {tierName} - Thank you for your purchase
            </p>
          </div>

          {/* Main Content Card */}
          <div className="bg-[#111] border border-[#FFFFFF] p-6 sm:p-8 lg:p-10" data-testid="success-content">
            
            {/* Blueprint Status */}
            <div className="flex items-start gap-4 mb-6 pb-6 border-b border-[#FFFFFF]">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#2CA8A0]/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-[#2CA8A0]" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl text-white font-medium mb-1">
                  Your Blueprint is Being Prepared
                </h2>
                <p className="text-[#888] text-sm sm:text-base">
                  We&apos;re generating your personalized Mexico relocation roadmap and sending it to your email right now.
                </p>
              </div>
            </div>

            {/* Email Notice - iter 185: near-instant delivery expectation.
                Webhook fires in 1-3s, Resend dispatch in another 1-2s, so
                the email is effectively already in the inbox by the time
                the user reads this. "2-3 minutes" set a false expectation
                that made people close the tab and wander off. */}
            <div className="flex items-start gap-4 mb-8" data-testid="success-email-notice">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFFFFF] flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-[#888]" />
              </div>
              <div>
                <p className="text-white text-sm sm:text-base mb-2">
                  <strong>Check your inbox now</strong> - it&apos;s already on its way.
                </p>
                <p className="text-[#666] text-sm">
                  You&apos;ll get your Blueprint PDF plus a link to set your password and open the Client Portal. If it&apos;s not there in a minute, check spam.
                </p>
              </div>
            </div>

            {/* PRIMARY CTA - iter 145: route to Blueprint intake to start
                the personalized Blueprint. Auto-redirect happens via the
                useEffect above; this button is the fallback / fast path. */}
            <Link
              to={`/relocation/blueprint${paymentEmail ? `?email=${encodeURIComponent(paymentEmail)}&paid=1&tier=${tier}` : `?paid=1&tier=${tier}`}`}
              className="block w-full bg-[#2CA8A0] text-black font-bold py-5 px-6 text-lg hover:bg-[#2CA8A0] transition-all text-center"
              data-testid="start-blueprint-intake-btn"
            >
              <span className="flex items-center justify-center gap-3">
                Start Your Blueprint
                <ArrowRight className="w-6 h-6" />
              </span>
            </Link>

            {/* SECONDARY CTA - iter 186.c: one-click deeplink to the
                same /create-account URL the welcome_portal_access email
                sends. Fallback for buyers who close the tab or miss the
                email; identical destination + query params so a click
                here lands in the same password-set flow. */}
            <Link
              to={createAccountUrl}
              className="block w-full border border-[#2CA8A0] text-[#2CA8A0] font-medium py-3 px-6 text-sm sm:text-base mt-3 hover:bg-[#2CA8A0] hover:text-black transition-all text-center"
              data-testid="set-password-cta"
            >
              <span className="flex items-center justify-center gap-2">
                <KeyRound className="w-4 h-4" />
                Set your password
              </span>
            </Link>

            {/* Secondary Text */}
            <p className="text-center text-[#555] text-xs sm:text-sm mt-4">
              Or close this tab and check your email later - the link will be waiting for you.
            </p>
          </div>

          {/* What's Included Section */}
          <div className="mt-8">
            <h3 className="text-[#0A1F2E] text-base sm:text-lg font-medium mb-4 text-center">What&apos;s waiting for you in the Portal:</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 bg-[#111] border border-[#FFFFFF] p-4">
                <FileText className="w-5 h-5 text-[#2CA8A0]" />
                <span className="text-[#aaa] text-sm">Personalized 7-Page Blueprint PDF</span>
              </div>
              <div className="flex items-center gap-3 bg-[#111] border border-[#FFFFFF] p-4">
                <ListChecks className="w-5 h-5 text-[#2CA8A0]" />
                <span className="text-[#aaa] text-sm">Interactive Relocation Checklist</span>
              </div>
              <div className="flex items-center gap-3 bg-[#111] border border-[#FFFFFF] p-4">
                <Sparkles className="w-5 h-5 text-[#2CA8A0]" />
                <span className="text-[#aaa] text-sm">Resource Hub (27+ vetted tools)</span>
              </div>
              <div className="flex items-center gap-3 bg-[#111] border border-[#FFFFFF] p-4">
                <MessageSquare className="w-5 h-5 text-[#2CA8A0]" />
                <span className="text-[#aaa] text-sm">Unlimited Assistant</span>
              </div>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-8 flex items-center justify-center gap-2 text-[#555] text-xs">
            <Shield className="w-4 h-4" />
            <span>Secure payment processed by Stripe. Your data is protected.</span>
          </div>

          {/* Contact Support */}
          <div className="mt-6 text-center">
            <p className="text-[#444] text-xs">
              Questions? Contact us at{' '}
              <a href="mailto:support@mundalo.com" className="text-[#2CA8A0] hover:underline">
                support@mundalo.com
              </a>
              {' '}or{' '}
              <a href="https://wa.me/14692131070" target="_blank" rel="noopener noreferrer" className="text-[#2CA8A0] hover:underline">
                WhatsApp
              </a>
            </p>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
